import { getErrorCauseChain } from '@/lib/error-cause-chain'
import { isRecord } from '@/lib/utils'

export const ADMIN_BUILD_CHECK_URL_ENV_KEY = 'VITE_ADMIN_BUILD_CHECK_URL'
export const ADMIN_BUILD_CHECK_MIN_INTERVAL_MS = 60_000
export const ADMIN_BUILD_REFRESH_TOAST_ID = 'admin-build-update-available'

type AdminBuildEnv = Readonly<Record<string, string | undefined>>

export type AdminBuildMetadata = {
  buildId: string
  [key: string]: unknown
}

export type FetchLatestAdminBuildMetadata = () => Promise<AdminBuildMetadata | null>

export type AdminBuildCheckResult =
  | { status: 'skipped' }
  | { status: 'throttled' }
  | { status: 'current'; latestBuildId: string | null }
  | { status: 'update-available'; latestBuildId: string }
  | { status: 'error'; error: unknown }

// Browser and Vite wordings for a code-split chunk (or its CSS) that can no longer load.
const CHUNK_LOAD_ERROR_MESSAGES = [
  'failed to fetch dynamically imported module', // Chromium
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari
  'unable to preload css', // Vite CSS preload
  'chunkloaderror', // older bundler wording, kept from DefaultError's original check
  'loading chunk',
]

let adminBuildStale = false
let pendingStaleConfirmation: Promise<boolean> | null = null

function errorText(error: object): string {
  const name = 'name' in error && typeof error.name === 'string' ? error.name : ''
  const message = 'message' in error && typeof error.message === 'string' ? error.message : ''

  return `${name} ${message}`
}

export function isChunkLoadErrorMessage(text: string): boolean {
  const normalizedText = text.toLowerCase()

  return CHUNK_LOAD_ERROR_MESSAGES.some((message) => normalizedText.includes(message))
}

export function isChunkLoadError(error: unknown): boolean {
  if (typeof error === 'string') {
    return isChunkLoadErrorMessage(error)
  }

  return getErrorCauseChain(error).some((currentError) =>
    isChunkLoadErrorMessage(errorText(currentError))
  )
}

/**
 * True once this tab knows a newer build replaced it (admin-build.json reports a different build
 * id), so its old chunks may be gone from the CDN.
 */
export function isAdminBuildStale(): boolean {
  return adminBuildStale
}

export function markAdminBuildStale(): void {
  adminBuildStale = true
}

/**
 * A chunk that fails to load usually means a deploy replaced this tab's build, but it can also be
 * a broken deploy of the current build. Mark the tab stale only once admin-build.json confirms a
 * newer build, so a real asset outage keeps reaching Sentry.
 */
export function confirmAdminBuildStale(
  getUpdate: () => Promise<AdminBuildMetadata | null> = getAdminBuildUpdateMetadata
): Promise<boolean> {
  if (isAdminBuildStale()) {
    return Promise.resolve(true)
  }

  // A deploy usually breaks several chunks at once; one admin-build.json check answers them all.
  pendingStaleConfirmation ??= getUpdate()
    .then((update) => {
      if (!update) {
        return false
      }

      markAdminBuildStale()
      return true
    })
    .catch(() => false)
    .finally(() => {
      pendingStaleConfirmation = null
    })

  return pendingStaleConfirmation
}

export function normalizeAdminBuildId(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return null
  }

  const normalized = String(value).trim()

  return normalized || null
}

export function getCurrentAdminBuildId(env?: AdminBuildEnv): string | null {
  if (env) {
    return (
      normalizeAdminBuildId(env.VITE_ADMIN_BUILD_ID) ||
      normalizeAdminBuildId(env.VITE_AWS_JOB_ID) ||
      normalizeAdminBuildId(env.VITE_AWS_COMMIT_ID)
    )
  }

  return (
    normalizeAdminBuildId(import.meta.env.VITE_ADMIN_BUILD_ID) ||
    normalizeAdminBuildId(import.meta.env.VITE_AWS_JOB_ID) ||
    normalizeAdminBuildId(import.meta.env.VITE_AWS_COMMIT_ID)
  )
}

export function getAdminBuildCheckUrl(env?: AdminBuildEnv): string | null {
  if (env) {
    return normalizeAdminBuildId(env[ADMIN_BUILD_CHECK_URL_ENV_KEY])
  }

  return normalizeAdminBuildId(import.meta.env.VITE_ADMIN_BUILD_CHECK_URL)
}

export function parseAdminBuildMetadata(metadata: unknown): AdminBuildMetadata | null {
  if (!isRecord(metadata)) {
    return null
  }

  const buildId =
    normalizeAdminBuildId(metadata.buildId) ||
    normalizeAdminBuildId(metadata.jobId) ||
    normalizeAdminBuildId(metadata.commitId)

  if (!buildId) {
    return null
  }

  return {
    ...metadata,
    buildId,
  }
}

export function buildAdminBuildCheckUrl(url: string, cacheBust: string | number = Date.now()) {
  const separator = url.includes('?') ? '&' : '?'

  return `${url}${separator}v=${encodeURIComponent(cacheBust)}`
}

export async function fetchLatestAdminBuildMetadata({
  fetchImpl = globalThis.fetch,
  url = getAdminBuildCheckUrl(),
  cacheBust = Date.now(),
}: {
  fetchImpl?: typeof fetch
  url?: string | null
  cacheBust?: string | number
} = {}): Promise<AdminBuildMetadata | null> {
  if (typeof fetchImpl !== 'function') {
    return null
  }

  const checkUrl = normalizeAdminBuildId(url)

  if (!checkUrl) {
    return null
  }

  const response = await fetchImpl(buildAdminBuildCheckUrl(checkUrl, cacheBust), {
    // Keep this as a simple cross-origin GET; cache-busting plus the response
    // Cache-Control avoids stale data without triggering a browser preflight.
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response?.ok) {
    return null
  }

  const metadata: unknown = await response.json()

  return parseAdminBuildMetadata(metadata)
}

export async function getAdminBuildUpdateMetadata({
  currentBuildId = getCurrentAdminBuildId(),
  fetchLatest = fetchLatestAdminBuildMetadata,
}: {
  currentBuildId?: string | null
  fetchLatest?: FetchLatestAdminBuildMetadata
} = {}): Promise<AdminBuildMetadata | null> {
  const normalizedCurrentBuildId = normalizeAdminBuildId(currentBuildId)

  if (!normalizedCurrentBuildId) {
    return null
  }

  try {
    const metadata = await fetchLatest()
    const latestBuildId = normalizeAdminBuildId(metadata?.buildId)

    if (!latestBuildId || latestBuildId === normalizedCurrentBuildId) {
      return null
    }

    return {
      ...metadata,
      buildId: latestBuildId,
    }
  } catch {
    return null
  }
}

export function createAdminBuildUpdateChecker({
  currentBuildId = getCurrentAdminBuildId(),
  fetchLatest = fetchLatestAdminBuildMetadata,
  minIntervalMs = ADMIN_BUILD_CHECK_MIN_INTERVAL_MS,
  now = () => Date.now(),
  onUpdateAvailable,
}: {
  currentBuildId?: string | null
  fetchLatest?: FetchLatestAdminBuildMetadata
  minIntervalMs?: number
  now?: () => number
  onUpdateAvailable?: (metadata: AdminBuildMetadata) => void
} = {}) {
  let inFlightCheck: Promise<AdminBuildCheckResult> | null = null
  let lastCheckAt = Number.NEGATIVE_INFINITY
  let updateAvailable = false
  const normalizedCurrentBuildId = normalizeAdminBuildId(currentBuildId)

  async function check({
    force = false,
  }: { force?: boolean } = {}): Promise<AdminBuildCheckResult> {
    if (!normalizedCurrentBuildId || updateAvailable) {
      return { status: 'skipped' }
    }

    const checkedAt = now()
    if (!force && checkedAt - lastCheckAt < minIntervalMs) {
      return { status: 'throttled' }
    }

    if (inFlightCheck) {
      return inFlightCheck
    }

    lastCheckAt = checkedAt
    inFlightCheck = Promise.resolve(fetchLatest())
      .then((metadata): AdminBuildCheckResult => {
        const latestBuildId = normalizeAdminBuildId(metadata?.buildId)

        if (metadata && latestBuildId && latestBuildId !== normalizedCurrentBuildId) {
          updateAvailable = true
          markAdminBuildStale()
          onUpdateAvailable?.(metadata)

          return { status: 'update-available', latestBuildId }
        }

        return { status: 'current', latestBuildId }
      })
      .catch((error: unknown): AdminBuildCheckResult => ({ status: 'error', error }))
      .finally(() => {
        inFlightCheck = null
      })

    return inFlightCheck
  }

  return {
    check,
    hasUpdateAvailable: () => updateAvailable,
  }
}
